import React, { useEffect } from "react";
import { useTransitionProductStatusMutation } from "../../../../hooks/useAdminProductCatalog";
import type { BillingProduct, ProductStatus } from "../../../../types/adminBilling";
import { PRODUCT_STATUS_LABELS } from "./constants";

export interface StatusTransitionModalProps {
  product: BillingProduct;
  targetStatus: ProductStatus;
  brandId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const StatusTransitionModal: React.FC<StatusTransitionModalProps> = ({
  product,
  targetStatus,
  brandId,
  onClose,
  onSuccess,
}) => {
  const transitionMutation = useTransitionProductStatusMutation(
    product.id,
    brandId
  );

  const isIrreversible =
    product.status === "ACTIVE" && targetStatus === "ARCHIVED";

  // Escape-to-close
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  const handleConfirm = () => {
    transitionMutation.mutate(
      { status: targetStatus },
      {
        onSuccess: () => {
          onSuccess();
          onClose();
        },
      }
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b border-neutral-200">
          <h3 className="text-lg font-semibold text-neutral-900">
            Zmiana statusu produktu
          </h3>
        </div>
        <div className="p-6">
          {isIrreversible ? (
            <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
              <p className="text-red-800 text-sm font-medium">
                Archiwizacja jest nieodwracalna. Istniejące subskrypcje pozostaną
                aktywne. Kontynuować?
              </p>
            </div>
          ) : (
            <p className="text-neutral-700 text-sm mb-4">
              Czy na pewno chcesz zmienić status produktu "{product.name}" z{" "}
              <strong>{PRODUCT_STATUS_LABELS[product.status as ProductStatus]}</strong> na{" "}
              <strong>{PRODUCT_STATUS_LABELS[targetStatus]}</strong>?
            </p>
          )}
        </div>
        <div className="px-6 py-4 border-t border-neutral-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-neutral-600 hover:text-neutral-800 font-medium"
          >
            Anuluj
          </button>
          <button
            onClick={handleConfirm}
            disabled={transitionMutation.isPending}
            className={`px-4 py-2 text-sm rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-white ${
              isIrreversible
                ? "bg-red-600 hover:bg-red-700"
                : "bg-primary-600 hover:bg-primary-700"
            }`}
          >
            {transitionMutation.isPending ? "Zmiana..." : "Potwierdź"}
          </button>
        </div>
      </div>
    </div>
  );
};
